// GST InvoiceNow implementation date calculator. Pure logic, no UI, so every branch is unit-tested
// (tests/calculator.test.ts) against the IRAS FAQ's own examples. It is indicative only: the date in IRAS's
// MyTaxMail notification always wins.
import { claim, phases, supplyBands, type Claim, type Phase } from '../content/facts'

/** Which months your prescribed accounting periods end in (FAQ Q1.11), or monthly periods (FAQ Q1.10). */
export type PeriodEnds = 'mar-jun-sep-dec' | 'jan-apr-jul-oct' | 'feb-may-aug-nov' | 'monthly'

export interface Returns2025 {
  periodEnds: PeriodEnds
  /** S$ totals across all prescribed accounting periods ending in calendar year 2025. Nil returns count as S$0. */
  standardRated: number
  zeroRated: number
  exempt: number
  /** Set when those periods cover less than a full year (e.g. registered in 2025): days covered, for the 365-day equivalent. */
  daysCovered?: number
}

export type CalcInput =
  | { kind: 'notRegistered' }
  | { kind: 'excluded' }
  /** Already GST-registered with returns for periods ending in 2025 (or registered before 2026 without them: returns = null). */
  | { kind: 'existing'; returns: Returns2025 | null }
  /** Applying, or applied, for voluntary registration. A compulsory application approved as voluntary counts here (FAQ Q1.6). */
  | { kind: 'voluntary'; applicationDate: string; newlyIncorporated: boolean; redomiciled: boolean; returns: Returns2025 | null }
  | { kind: 'compulsory'; applicationDate: string; returns: Returns2025 | null }
  | { kind: 'group' }

export type Outcome = 'from-registration' | 'phase' | 'iras-will-notify' | 'follow-group' | 'not-required'

export interface CalcResult {
  outcome: Outcome
  /** Headline date, e.g. "1 Apr 2029" or "From registration". */
  date: string
  phase?: Phase
  totalAnnualSupplies?: number
  /** True when the total is a 365-day equivalent of a part year (FAQ Q1.7, Q1.8). */
  extrapolated?: boolean
  /** Human-readable 12-month window to add up, from FAQ Q1.11. */
  window?: string
  periodEnds?: PeriodEnds
  /** The reasons for this result, each with its source. */
  reasons: Claim[]
}

const P = (id: Phase['id']) => phases.find(p => p.id === id)!

export const windows: Record<PeriodEnds, [string, Claim]> = {
  'mar-jun-sep-dec': ['January 2025 to December 2025', claim('If your periods end in Mar, Jun, Sep and Dec, add up supplies from January 2025 to December 2025.', 'irasFaq', 'Q1.11')],
  'jan-apr-jul-oct': ['November 2024 to October 2025', claim('If your periods end in Jan, Apr, Jul and Oct, add up supplies from November 2024 to October 2025.', 'irasFaq', 'Q1.11')],
  'feb-may-aug-nov': ['December 2024 to November 2025', claim('If your periods end in Feb, May, Aug and Nov, add up supplies from December 2024 to November 2025.', 'irasFaq', 'Q1.11')],
  monthly: ['January 2025 to December 2025', claim('Total annual supplies cover all prescribed accounting periods ending in calendar year 2025.', 'irasFaq', 'Q1.10')],
}

/** Every sourced reason the calculator can give (translated under calc.reason.<id>). */
export const reasons = {
  sum: claim('Total annual supplies are the standard-rated, zero-rated and exempt supplies made in all prescribed accounting periods ending in calendar year 2025.', 'irasFaq', 'Q1.10'),
  nil: claim('Nil GST returns still count as returns filed, so total annual supplies are S$0 and the requirement applies from 1 Apr 2028.', 'irasFaq', 'Q1.12'),
  extrapolate: claim('If your returns ending in 2025 cover only part of the year, you may extrapolate the supplies to a 365-day equivalent to find your phase.', 'irasFaq', 'Q1.7, Q1.8'),
  noReturns: claim('If you have no GST returns for periods ending in calendar year 2025, IRAS notifies you of your implementation date separately.', 'irasFaq', 'Q1.7, Q1.8'),
  noReturnsPage: claim('Businesses without GST returns for periods ending in 2025 will have their implementation date communicated later.', 'irasPage'),
  volFrom2026: claim('Businesses applying for voluntary GST registration on or after 1 Apr 2026 must comply, regardless of incorporation date or business structure.', 'irasFaq', 'Q1.5'),
  volCompulsoryApprovedAsVoluntary: claim('A compulsory registration application approved on a voluntary basis must also comply.', 'irasFaq', 'Q1.6'),
  newInc: claim('From 1 Nov 2025, newly incorporated companies applying for voluntary registration within 6 months of incorporation must comply. The application date counts, not the approval date.', 'irasFaq', 'Q1.7'),
  redomiciled: claim('A company redomiciled to Singapore is not treated as newly incorporated, so it is not in the 1 Nov 2025 phase.', 'irasFaq', 'Q1.8'),
  compulsory2028: claim('Businesses that apply for compulsory GST registration on or after 1 Apr 2028 must comply from registration.', 'irasPage'),
  group: claim('A new voluntary GST registrant added to an existing GST group follows the group’s phase. Only Excluded Businesses in the group (such as overseas entities) need not transmit invoice data.', 'irasFaq', 'Q7.2'),
  excluded: claim('Excluded Businesses (overseas entities, and businesses registered wholly because of the reverse charge regime) are not required to comply.', 'irasFaq', 'Q9.2'),
  notRegistered: claim('The requirement applies to GST-registered businesses.', 'irasPage'),
}

type BandPhase = 'p3' | 'p4' | 'p5' | 'p6'
/** One sourced reason per supply band (translated under calc.reason.band_<phase>). */
export const bandReasons = Object.fromEntries(supplyBands.map(b => {
  const p = P(b.phase)
  return [b.phase, { sourceUrl: b.sourceUrl, sourceLabel: b.sourceLabel, lastVerified: b.lastVerified, text: `Existing GST-registered businesses with total annual supplies ${b.label} must comply from ${p.label}.` }]
})) as Record<BandPhase, Claim>

export const total = (r: Returns2025) => r.standardRated + r.zeroRated + r.exempt

/** 365-day equivalent when the 2025 returns cover only part of a year. */
export function annualised(r: Returns2025): { amount: number; extrapolated: boolean } {
  const t = total(r)
  if (!r.daysCovered || r.daysCovered >= 365) return { amount: t, extrapolated: false }
  return { amount: Math.round(t * 365 / r.daysCovered), extrapolated: true }
}

export function phaseForSupplies(amount: number): Phase {
  return P(supplyBands.find(b => amount <= b.max)!.phase)
}

function fromReturns(returns: Returns2025 | null, lead: Claim[]): CalcResult {
  if (!returns) return { outcome: 'iras-will-notify', date: 'IRAS will tell you', reasons: [...lead, reasons.noReturns, reasons.noReturnsPage] }
  const { amount, extrapolated } = annualised(returns)
  const phase = phaseForSupplies(amount)
  const [window, windowClaim] = windows[returns.periodEnds]
  const out = [...lead, reasons.sum, windowClaim]
  if (extrapolated) out.push(reasons.extrapolate)
  if (amount === 0) out.push(reasons.nil)
  out.push(bandReasons[phase.id as BandPhase])
  return { outcome: 'phase', date: phase.label, phase, totalAnnualSupplies: amount, extrapolated, window, periodEnds: returns.periodEnds, reasons: out }
}

export function calculate(input: CalcInput): CalcResult {
  switch (input.kind) {
    case 'notRegistered':
      return { outcome: 'not-required', date: 'Not required', reasons: [reasons.notRegistered] }
    case 'excluded':
      return { outcome: 'not-required', date: 'Not required', reasons: [reasons.excluded] }
    case 'group':
      return { outcome: 'follow-group', date: 'Your group’s date', reasons: [reasons.group] }
    case 'existing':
      return fromReturns(input.returns, [])
    case 'voluntary': {
      const d = input.applicationDate
      if (d >= P('p2').startIso) return { outcome: 'from-registration', date: 'From registration', phase: P('p2'), reasons: [reasons.volFrom2026, reasons.volCompulsoryApprovedAsVoluntary] }
      if (d >= P('p1').startIso && input.newlyIncorporated && !input.redomiciled) return { outcome: 'from-registration', date: 'From registration', phase: P('p1'), reasons: [reasons.newInc] }
      // Not caught by the voluntary phases: falls under the existing-registrant rules (FAQ Q1.7, Q1.8).
      const lead = input.redomiciled ? [reasons.redomiciled] : input.newlyIncorporated ? [reasons.newInc] : []
      return fromReturns(input.returns, lead)
    }
    case 'compulsory': {
      if (input.applicationDate >= P('p3').startIso) return { outcome: 'from-registration', date: 'From registration', phase: P('p3'), reasons: [reasons.compulsory2028] }
      return fromReturns(input.returns, [])
    }
  }
}
